import { useTranslation } from "react-i18next";
import { getInboxView } from "../../../utils";
import { useCallback, useMemo, useRef } from "react";
import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeQueries,
  useTellescopeQuery,
  useUpdateThreadInboxStatus,
} from "@tellescope/tanstack-query";
import { cn, formatDateTime, formatEmailPreview, formatEmailTitle, formatName, formatPhone } from "@tellescope/utils";
import { buildMessagingThreadLink } from "../../../../../utils";
import { Link } from "../../../../../components";
import { ChannelIcon } from "../ChannelIcon";
import { Badge } from "@tellescope/ui";
import type { InboxThread } from "@tellescope/types-models";
import { Clock } from "lucide-react";
import { InboxStatusBadge } from "../..";
import { useLocation } from "react-router-dom";
import { useUpdateEnduserInSidebar } from "../../../../../hooks";

export interface ThreadLike
  extends Pick<
    InboxThread,
    | "outboundTimestamp"
    | "inboxStatus"
    | "enduserIds"
    | "type"
    | "id"
    | "title"
    | "outboundPreview"
    | "preview"
    | "tags"
    | "phoneNumber"
    | "enduserPhoneNumber"
    | "scheduledMessageIds"
    | "threadId"
    | "readBy"
  > {
  timestamp?: Date | "";
}

export interface InboxThreadListItemProps<Thread extends ThreadLike> {
  thread: Thread;
  isSelected: boolean;
  isDraft?: boolean;
  className?: string;
  outboundOnly?: boolean;
}

export const InboxThreadListDefaultItem = <Thread extends ThreadLike>({
  thread,
  isSelected,
  isDraft,
  className,
  outboundOnly,
}: InboxThreadListItemProps<Thread>) => {
  const { t } = useTranslation("inbox");
  const location = useLocation();
  const view = getInboxView(location);
  const { currentUser } = useAuth();
  const updateEnduserInSidebar = useUpdateEnduserInSidebar();
  const isUnread = currentUser.id ? !thread.readBy?.[currentUser.id] : false;
  const showOutbound = useMemo(() => {
    if (outboundOnly) return true;

    const outboundTimestamp = thread.outboundTimestamp || new Date(0);
    const inboundTimestamp = thread.timestamp || new Date(0);
    return outboundTimestamp.getTime() >= inboundTimestamp.getTime();
  }, [outboundOnly, thread.outboundTimestamp, thread.timestamp]);

  const endusers = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: thread.enduserIds,
    combine: (results) =>
      results.reduce<TellescopeModelFromName<"endusers">[]>((acc, result) => {
        if (result.data) {
          acc.push(result.data);
        }
        return acc;
      }, []),
  });

  const threadFromDraft = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      mdbFilter: {
        phoneNumber: thread.phoneNumber,
        enduserPhoneNumber: thread.enduserPhoneNumber,
      },
      limit: 1,
    },
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.at(0)?.threads.at(0),
    enabled: !!isDraft,
  });

  const threadId = useMemo(() => {
    if (isDraft) {
      return threadFromDraft.data?.id;
    }

    return thread.id;
  }, [isDraft, thread.id, threadFromDraft.data?.id]);

  const { updateInboxStatus } = useUpdateThreadInboxStatus({
    threadId: thread.id,
    metadata: thread,
  });

  const statusDropdownOpenRef = useRef(false);
  const handleStatusDropdownOpenChange = useCallback((open: boolean) => {
    statusDropdownOpenRef.current = open;
  }, []);

  const { outboundPreview, preview } = useMemo(() => {
    if (thread.type === "Email") {
      return {
        outboundPreview: thread.outboundPreview ? formatEmailPreview(thread.outboundPreview) : "",
        preview: formatEmailPreview(thread.preview),
      };
    }

    return {
      outboundPreview: thread.outboundPreview,
      preview: thread.preview,
    };
  }, [thread.outboundPreview, thread.preview, thread.type]);

  const previewIsFromScheduled =
    thread.type === "SMS"
      ? !!thread.scheduledMessageIds?.includes(thread.threadId)
      : thread.type === "Email" || thread.type === "Chat"
      ? !!thread.scheduledMessageIds?.length
      : false;
  const scheduledSMSQuery = useTellescopeQuery({
    model: "sms_messages",
    method: "getOne",
    request: thread.threadId,
    enabled: previewIsFromScheduled && thread.type === "SMS",
  });
  const scheduledEmailQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: thread.scheduledMessageIds?.at(0) ?? "",
    enabled: previewIsFromScheduled && thread.type === "Email",
  });
  const scheduledChatQuery = useTellescopeQuery({
    model: "chats",
    method: "getOne",
    request: thread.scheduledMessageIds?.at(0) ?? "",
    enabled: previewIsFromScheduled && thread.type === "Chat",
  });

  const formattedTimestamp = useMemo(() => {
    if (previewIsFromScheduled) {
      switch (thread.type) {
        case "SMS":
          return scheduledSMSQuery.data?.sendAt
            ? formatDateTime(scheduledSMSQuery.data.sendAt, "MM/DD/YYYY at hh:mm a", "always")
            : "";
        case "Email":
          return scheduledEmailQuery.data?.sendAt
            ? formatDateTime(scheduledEmailQuery.data.sendAt, "MM/DD/YYYY at hh:mm a", "always")
            : "";
        case "Chat":
          return scheduledChatQuery.data?.sendAt
            ? formatDateTime(scheduledChatQuery.data.sendAt, "MM/DD/YYYY at hh:mm a", "always")
            : "";
      }
    }

    return showOutbound && thread.outboundTimestamp
      ? formatDateTime(thread.outboundTimestamp, "MM/DD/YYYY at hh:mm a", "always")
      : thread.timestamp
      ? formatDateTime(thread.timestamp, "MM/DD/YYYY at hh:mm a", "always")
      : "";
  }, [
    previewIsFromScheduled,
    scheduledChatQuery.data?.sendAt,
    scheduledEmailQuery.data?.sendAt,
    scheduledSMSQuery.data?.sendAt,
    showOutbound,
    thread.outboundTimestamp,
    thread.timestamp,
    thread.type,
  ]);

  return (
    <Link
      className={cn(
        "block p-3 rounded-lg border space-y-2 cursor-pointer transition-colors hover:bg-muted/50",
        isUnread && "bg-muted",
        isSelected ? "border-primary" : "border-border",
        className
      )}
      data-selected={isSelected ? "true" : undefined}
      to={
        threadId
          ? buildMessagingThreadLink({
              view,
              channel: thread.type,
              threadId,
              draftId: isDraft ? thread.id : undefined,
            })
          : ""
      }
      onClickCapture={(e) => {
        if (statusDropdownOpenRef.current) {
          e.preventDefault();
          return;
        }
        const enduserId = endusers?.[0]?.id;
        updateEnduserInSidebar(enduserId);
      }}
      keepSearch
      data-trackingid="messaging-open-thread"
    >
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
            <span className={`text-sm ${isUnread ? "font-bold" : "font-semibold"} truncate`}>
              {endusers.map((name) => formatName(name)).join(", ")}
            </span>
            {isUnread && (
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0" title={t("Unread message")}></div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!!formattedTimestamp && (
              <span
                className={`flex gap-1 items-center text-xs ${
                  isUnread ? "text-foreground font-medium" : "text-muted-foreground"
                }`}
              >
                {!!previewIsFromScheduled && <Clock size={12} />}
                {formattedTimestamp}
              </span>
            )}
          </div>
        </div>
        <div className={`flex items-center gap-1.5 text-xs ${isUnread ? "font-semibold" : "font-medium"}`}>
          <ChannelIcon channel={thread.type} />
          <span>
            {thread.type === "Email"
              ? formatEmailTitle(thread.title)
              : thread.type === "Chat"
              ? thread.title
              : formatPhone(thread.title || thread.enduserPhoneNumber)}
          </span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground truncate">{showOutbound ? outboundPreview : preview}</p>

      {(!!thread.tags?.length || !!thread.inboxStatus) && (
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            {thread.tags?.slice(0, 2).map((tag) => (
              <Badge key={`tag-${tag}`} variant="secondary" className={cn("border max-w-20", isUnread && "bg-white")}>
                <span className="truncate">{tag}</span>
              </Badge>
            ))}
            {(thread.tags?.length ?? 0) > 2 && (
              <Badge variant="outline" className="text-xs">
                +{(thread.tags?.length ?? 0) - 2}
              </Badge>
            )}
          </div>
          {!!thread.inboxStatus && (
            <InboxStatusBadge
              inboxStatus={thread.inboxStatus}
              onStatusChange={(status) =>
                updateInboxStatus({
                  inboxStatus: status,
                })
              }
              onDropdownOpenChange={handleStatusDropdownOpenChange}
            />
          )}
        </div>
      )}
    </Link>
  );
};
